import { CSSObject } from '@emotion/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { kuiperClassNames, WithTooltip } from '@opallabs/kuiper'
import Button from '@opallabs/kuiper/src/Button'
import {
  GetSectionPostOptionsProps,
  OnAddSectionProps,
  OnDragDropProps,
  OnShowAssetPickerProps,
  SectionPostOptions,
} from '../Content/helpers/types'
import type { ModuleDefinition, Module } from '../Content/helpers/types'
import { ExpandedCarouselBody } from './ExpandedCarouselBody'
import { ScrollableCarouselBody } from './ScrollableCarouselBody'
import { styles as sharedStyles } from './sharedStyles/styles'

export type OverrideStyleProps = {
  activeViewportStyles?: () => CSSObject
  arrowStyles?: () => CSSObject
  viewportStyles?: () => CSSObject
  sectionStyles?: () => CSSObject
}

export interface ModularCarouselProps {
  addComponentText?: string
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  carouselWidth: number
  displacementHeight?: number
  getModule?: (args: GetSectionPostOptionsProps) => SectionPostOptions
  isExpanded: boolean
  isPresenting: boolean
  maxSections?: number
  modules: Module[]
  overrideStyles?: OverrideStyleProps
  onAddSection: (args: OnAddSectionProps) => void
  onDeleteSection: (indexToRemove: number) => void
  onDragDrop: OnDragDropProps
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  readOnly: boolean
  updateSectionPostOption: (index: number, value: string, name: string) => void
}

export const ModularCarousel: React.FC<ModularCarouselProps> = ({
  addComponentText = 'Add Section',
  allProps,
  availableModules,
  carouselWidth,
  getModule,
  isExpanded,
  isPresenting,
  maxSections,
  modules,
  overrideStyles,
  onAddSection,
  onDeleteSection,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  readOnly,
  updateSectionPostOption,
}) => {
  const atMaxModuleLength = maxSections && modules?.length >= maxSections
  const [tooltipActive, setTooltipActive] = useState(-1)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [shouldExitExpanded, setShouldExitExpanded] = useState(false)
  const expandedRef = useRef(isExpanded)
  const timer: { current: unknown } = useRef(null)

  const onTooltipToggle = (index: number) => {
    const newIndex = tooltipActive >= 0 && tooltipActive === index ? -1 : index
    setTooltipActive(newIndex)
  }

  const deleteSection = (index: number) => {
    onDeleteSection(index)
    setTooltipActive(-1)
  }

  useEffect(() => {
    // go back to first index if isExpanded gets toggled
    setCurrentIndex(0)
    // leave time to show Expanded Carousel exit animation
    if (isExpanded) {
      expandedRef.current = true
    } else {
      setShouldExitExpanded(true)
      timer.current = setTimeout(() => {
        expandedRef.current = false
        setShouldExitExpanded(false)
      }, 300)
    }
  }, [isExpanded])

  return (
    <Fragment>
      {!readOnly && !isPresenting && (
        <div
          className={kuiperClassNames(
            `add-section-button${atMaxModuleLength ? '-disabled' : ''}`
          )}
          css={sharedStyles.getSectionBtnStyling(readOnly)}
        >
          <WithTooltip
            disabled={!atMaxModuleLength}
            content={`There is a maximum of ${maxSections} section${
              (maxSections || 0) > 1 ? 's' : ''
            }`}
            tooltipOptions={{ position: 'top' }}
          >
            <Button
              className={kuiperClassNames('asset-carousel-add-section')}
              ariaLabel="Add New Carousel Section"
              icon="plus-solid"
              label={addComponentText}
              type="secondary"
              disabled={!!atMaxModuleLength}
              onClick={() => onAddSection({})}
            />
          </WithTooltip>
        </div>
      )}
      {expandedRef.current ? (
        <ExpandedCarouselBody
          allProps={allProps}
          availableModules={availableModules}
          carouselWidth={carouselWidth}
          getModule={getModule}
          isPresenting={isPresenting}
          tooltipActive={tooltipActive}
          modules={modules || []}
          shouldExit={shouldExitExpanded}
          onDeleteSection={(index: number) => deleteSection(index)}
          onDragDrop={onDragDrop}
          onExpandAsset={onExpandAsset}
          onRemoveAsset={onRemoveAsset}
          onShowAssetPicker={onShowAssetPicker}
          onTooltipToggle={onTooltipToggle}
          readOnly={readOnly}
          updateSectionPostOption={updateSectionPostOption}
        />
      ) : (
        <ScrollableCarouselBody
          allProps={allProps}
          availableModules={availableModules}
          carouselWidth={carouselWidth}
          currentIndex={currentIndex}
          getModule={getModule}
          isPresenting={isPresenting}
          isTooltipActive={tooltipActive === currentIndex}
          modules={modules || []}
          onDeleteSection={(index: number) => deleteSection(index)}
          overrideStyles={overrideStyles}
          onDragDrop={onDragDrop}
          onExpandAsset={onExpandAsset}
          onRemoveAsset={onRemoveAsset}
          onShowAssetPicker={onShowAssetPicker}
          onTooltipToggle={onTooltipToggle}
          readOnly={readOnly}
          setCurrentIndex={setCurrentIndex}
          setIsTooltipActive={setTooltipActive}
          updateSectionPostOption={updateSectionPostOption}
        />
      )}
    </Fragment>
  )
}
